import { assign, fromPromise, raise, setup } from 'xstate';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import {
  acceptConflictCode,
  feeRefusalCode,
  isUncertainListingActionError,
  listingActionRefusalMessage,
  replyRefusalFeedback,
  type FeeRefusalCode,
} from '~/features/market/listing-search/errors';
import {
  MARKET_ACTION_TIMEOUT_MS,
  MARKET_READ_TIMEOUT_MS,
  marketReadTimeout,
} from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';

const REPLY_REFUSED = 'Your message was not sent. Review the text and try again.';

type Navigation<TFilter> =
  | { type: 'ON_LOAD' }
  | { type: 'ON_SELECT'; responseId: number }
  | { type: 'ON_DEEP_LINK'; responseId: number }
  | { type: 'ON_FILTER'; filter: TFilter }
  | { type: 'ON_INCLUDE_DECLINED'; includeDeclined: boolean };
export type OpportunityAction = {
  responseId: number;
  kind: 'reply' | 'accept' | 'withdraw';
  messageBody?: string;
  expectedNegotiationId?: number;
  /** [MCV-1889] The payer's acknowledged fee schedule, when a responder who pays accepts. */
  feeAuthorization?: FeeAuthorization;
};
export type OpportunitySendReceipt = { responseId: number; sequence: number };
export type RetainedReply = { messageBody: string; error: string };

export type OpportunitiesMachineConfig<
  TOpportunity extends { responseId: number; latestNegotiation?: { id: number } | null },
  TMessage,
  TFilter extends string,
> = {
  id: string;
  defaultFilter: TFilter;
  api: {
    getOpportunities: (
      filter: TFilter,
      signal?: AbortSignal,
      includeDeclined?: boolean
    ) => Promise<{ items: TOpportunity[]; totalCount: number }>;
    getMessages: (responseId: number, signal?: AbortSignal) => Promise<TMessage[]>;
    sendMessage: (
      responseId: number,
      messageBody: string,
      signal?: AbortSignal
    ) => Promise<TMessage>;
    accept: (
      responseId: number,
      expectedNegotiationId: number | undefined,
      signal?: AbortSignal,
      feeAuthorization?: FeeAuthorization
    ) => Promise<void>;
    withdraw: (responseId: number, signal?: AbortSignal) => Promise<void>;
  };
};

export type OpportunitiesMachineInput = {
  /**
   * [MCV-1549] Row a notification deep link asked for. Honoured on the first
   * completed load and then dropped — see `resolveSelectedOpportunity`.
   */
  initialResponseId?: number;
};

export type OpportunitiesContext<TOpportunity, TMessage, TFilter extends string> = {
  opportunities: TOpportunity[];
  totalCount: number;
  selectedOpportunity: TOpportunity | undefined;
  messages: TMessage[];
  filter: TFilter;
  /** [MCV-1850] Declined and withdrawn responses are hidden unless this is set. */
  includeDeclined: boolean;
  loading: boolean;
  messagesLoading: boolean;
  error: string | null;
  pendingResponseId: number | undefined;
  pendingAction: OpportunityAction | null;
  uncertainAction: OpportunityAction | null;
  retainedReplies: Record<number, RetainedReply | undefined>;
  uncertaintyReviewed: boolean;
  sendReceipt: OpportunitySendReceipt | null;
  queuedNavigation: Navigation<TFilter> | null;
  recoveryDeadlineAt: number | undefined;
  /**
   * [MCV-1889] The last Accept was refused for its fee schedule (428 / 409). The view takes it once
   * (ON_CONSUME_FEE_REFUSAL) to show the refetched schedule again instead of an error.
   */
  feeRefusal: FeeRefusalCode | null;
};

/**
 * Order matters: a pending deep link outranks the current selection, which
 * outranks the default first row.
 *
 * The link has to win. This load was very likely started *because* of it, and
 * the previous selection usually still exists in the new list — ranking the
 * survivor first would silently discard the notification the user just clicked,
 * which is the whole defect being fixed.
 *
 * `pendingResponseId` is dropped once a load completes, so it steers the
 * arrival from a notification and nothing after it. Keeping it live would mean
 * a filter change or a post-action reload silently yanked the user back to the
 * row they had navigated away from.
 */
const resolveSelectedOpportunity = <TOpportunity extends { responseId: number }>(
  current: TOpportunity | undefined,
  incoming: TOpportunity[],
  pendingResponseId?: number
): TOpportunity | undefined => {
  if (pendingResponseId != null) {
    // A miss falls through: the response may belong to someone else, or no
    // longer exist. Landing on the list beats rendering nothing.
    const requested = incoming.find((o) => o.responseId === pendingResponseId);
    if (requested) return requested;
  }
  if (current) {
    const reselected = incoming.find((o) => o.responseId === current.responseId);
    if (reselected) return reselected;
  }
  return incoming[0];
};

export function createOpportunitiesMachine<
  TOpportunity extends { responseId: number; latestNegotiation?: { id: number } | null },
  TMessage,
  TFilter extends string,
>(config: OpportunitiesMachineConfig<TOpportunity, TMessage, TFilter>) {
  return setup({
    types: {
      context: {} as OpportunitiesContext<TOpportunity, TMessage, TFilter>,
      input: {} as OpportunitiesMachineInput | undefined,
      events: {} as
        | { type: 'ON_LOAD' }
        | { type: 'ON_SELECT'; responseId: number }
        | { type: 'ON_DEEP_LINK'; responseId: number }
        | { type: 'ON_FILTER'; filter: TFilter }
        | { type: 'ON_INCLUDE_DECLINED'; includeDeclined: boolean }
        | { type: 'ON_SEND_MESSAGE'; messageBody: string }
        | { type: 'ON_EDIT_REPLY'; messageBody: string }
        | { type: 'ON_ACCEPT'; feeAuthorization?: FeeAuthorization }
        | { type: 'ON_WITHDRAW' }
        | { type: 'ON_CONSUME_FEE_REFUSAL' }
        | { type: 'ON_ACKNOWLEDGE_UNCERTAINTY' }
        | { type: 'ON_DISCARD_UNCERTAINTY' },
    },
    actors: {
      loadOpportunities: fromPromise(
        async ({
          input,
          signal,
        }: {
          input: {
            filter: TFilter;
            includeDeclined: boolean;
            pendingResponseId?: number;
            recoveryDeadlineAt?: number;
          };
          signal: AbortSignal;
        }): Promise<{ items: TOpportunity[]; totalCount: number; includeDeclined: boolean }> =>
          withRequestDeadline(
            async (requestSignal) => {
              const result = await config.api.getOpportunities(
                input.filter,
                requestSignal,
                input.includeDeclined
              );
              // [MCV-1850] A notification can name a response the default list hides
              // (declined or withdrawn). Look again with those included, and keep them
              // so the checkbox matches the list the user ends up with.
              const { pendingResponseId } = input;
              if (
                pendingResponseId != null &&
                !input.includeDeclined &&
                !result.items.some((o) => o.responseId === pendingResponseId)
              ) {
                const wider = await config.api.getOpportunities(input.filter, requestSignal, true);
                if (wider.items.some((o) => o.responseId === pendingResponseId))
                  return { ...wider, includeDeclined: true };
              }
              return { ...result, includeDeclined: input.includeDeclined };
            },
            marketReadTimeout(input.recoveryDeadlineAt),
            signal
          )
      ),
      loadMessages: fromPromise(
        async ({
          input,
          signal,
        }: {
          input: { responseId: number | undefined; recoveryDeadlineAt?: number };
          signal: AbortSignal;
        }): Promise<TMessage[]> => {
          if (input.responseId === undefined)
            throw new Error('No opportunity selected: cannot load messages without a responseId');
          const responseId = input.responseId;
          return withRequestDeadline(
            (requestSignal) => config.api.getMessages(responseId, requestSignal),
            marketReadTimeout(input.recoveryDeadlineAt),
            signal
          );
        }
      ),
      sendMessage: fromPromise(
        async ({
          input,
          signal,
        }: {
          input: { responseId: number | undefined; messageBody: string };
          signal: AbortSignal;
        }): Promise<TMessage> => {
          if (input.responseId === undefined)
            throw new Error('No opportunity selected: cannot send a message without a responseId');
          const responseId = input.responseId;
          return withRequestDeadline(
            (requestSignal) => config.api.sendMessage(responseId, input.messageBody, requestSignal),
            MARKET_ACTION_TIMEOUT_MS,
            signal
          );
        }
      ),
      updateStatus: fromPromise(
        async ({
          input,
          signal,
        }: {
          input: {
            responseId: number | undefined;
            action: 'ACCEPT' | 'WITHDRAW';
            expectedNegotiationId?: number;
            feeAuthorization?: FeeAuthorization;
          };
          signal: AbortSignal;
        }): Promise<void> => {
          if (input.responseId === undefined)
            throw new Error('No opportunity selected: cannot update status without a responseId');
          const responseId = input.responseId;
          return withRequestDeadline(
            (requestSignal) =>
              input.action === 'ACCEPT'
                ? config.api.accept(
                    responseId,
                    input.expectedNegotiationId,
                    requestSignal,
                    // Only a paying responder carries one; every other accept calls as it always did.
                    ...(input.feeAuthorization ? ([input.feeAuthorization] as const) : [])
                  )
                : config.api.withdraw(responseId, requestSignal),
            MARKET_ACTION_TIMEOUT_MS,
            signal
          );
        }
      ),
    },
    guards: {
      hasSelectedOpportunity: ({ context }) => context.selectedOpportunity !== undefined,
      canWrite: ({ context }) =>
        context.selectedOpportunity !== undefined && context.uncertainAction === null,
      isValidSelection: ({ context, event }) => {
        const { responseId } = event as { responseId: number };
        return context.opportunities.some((o) => o.responseId === responseId);
      },
      hasSelectableOpportunity: ({ context, event }) => {
        const { items } = (
          event as unknown as {
            output: { items: TOpportunity[]; totalCount: number; includeDeclined: boolean };
          }
        ).output;
        return (
          resolveSelectedOpportunity(
            context.selectedOpportunity,
            items,
            context.pendingResponseId
          ) !== undefined
        );
      },
    },
    actions: {
      setLoading: assign({ loading: true }),
      setMessagesLoading: assign({ messagesLoading: true }),
      clearError: assign({
        error: null,
        recoveryDeadlineAt: ({ context }) =>
          context.queuedNavigation ? context.recoveryDeadlineAt : undefined,
        queuedNavigation: null,
        uncertaintyReviewed: false,
      }),
      queueNavigation: assign({
        queuedNavigation: ({ context, event }) =>
          event.type === 'ON_LOAD' && context.queuedNavigation
            ? context.queuedNavigation
            : (event as Navigation<TFilter>),
      }),
      clearUncertainty: assign({ uncertainAction: null, uncertaintyReviewed: false, error: null }),
      captureAction: assign({
        pendingAction: ({ context, event }): OpportunityAction | null =>
          context.selectedOpportunity
            ? {
                responseId: context.selectedOpportunity.responseId,
                kind:
                  event.type === 'ON_SEND_MESSAGE'
                    ? 'reply'
                    : event.type === 'ON_ACCEPT'
                      ? 'accept'
                      : 'withdraw',
                messageBody: event.type === 'ON_SEND_MESSAGE' ? event.messageBody : undefined,
                expectedNegotiationId:
                  event.type === 'ON_ACCEPT'
                    ? context.selectedOpportunity.latestNegotiation?.id
                    : undefined,
                feeAuthorization: event.type === 'ON_ACCEPT' ? event.feeAuthorization : undefined,
              }
            : null,
        error: null,
        feeRefusal: null,
        uncertaintyReviewed: false,
      }),
    },
  }).createMachine({
    id: config.id,
    context: ({ input }) => ({
      opportunities: [] as TOpportunity[],
      totalCount: 0,
      selectedOpportunity: undefined,
      messages: [] as TMessage[],
      filter: config.defaultFilter,
      includeDeclined: false,
      loading: false,
      messagesLoading: false,
      error: null,
      pendingResponseId: input?.initialResponseId,
      pendingAction: null,
      uncertainAction: null,
      retainedReplies: {},
      uncertaintyReviewed: false,
      sendReceipt: null,
      queuedNavigation: null,
      recoveryDeadlineAt: undefined,
      feeRefusal: null,
    }),
    initial: '$_VIEW',
    on: {
      ON_CONSUME_FEE_REFUSAL: { actions: assign({ feeRefusal: null }) },
      ON_EDIT_REPLY: {
        guard: 'canWrite',
        actions: assign({
          retainedReplies: ({ context, event }) => {
            const responseId = context.selectedOpportunity?.responseId;
            const retained =
              responseId === undefined ? undefined : context.retainedReplies[responseId];
            return retained && responseId !== undefined
              ? {
                  ...context.retainedReplies,
                  [responseId]: { ...retained, messageBody: event.messageBody },
                }
              : context.retainedReplies;
          },
        }),
      },
      ON_LOAD: {
        target: '.$_LOAD',
        actions: ['setLoading', 'clearError'],
      },
      ON_FILTER: {
        target: '.$_LOAD',
        actions: [assign({ filter: ({ event }) => event.filter }), 'setLoading', 'clearError'],
      },
      ON_INCLUDE_DECLINED: {
        target: '.$_LOAD',
        actions: [
          assign({ includeDeclined: ({ event }) => event.includeDeclined }),
          'setLoading',
          'clearError',
        ],
      },
      // [MCV-1549] A notification arriving while this surface is already
      // mounted. Machine input only covers the first mount; clicking a second
      // notification for the same route swaps the query string and nothing
      // else, so the new id has to arrive as an event.
      //
      // One transition, no "already loaded" fast path. That fast path read
      // `context.opportunities`, which is stale while an ON_FILTER load is in
      // flight: it would cancel that load, select a row the new filter
      // excludes, leave `filter` untouched, and the next reload would drop the
      // row again. Re-entering $_LOAD cancels the in-flight invocation and
      // refetches under the reset filter, so the list the user ends up with
      // actually contains what the link named.
      //
      // The filter resets because a query under the user's `active`/`accepted`
      // filter cannot return a row that filter excludes — resolveSelected-
      // Opportunity would fall through to incoming[0] and the notification
      // would open an unrelated conversation. An explicit deep link outranks a
      // filter set earlier.
      ON_DEEP_LINK: {
        target: '.$_LOAD',
        actions: [
          assign({
            pendingResponseId: ({ event }) => event.responseId,
            // Optimistic: if the row is already on screen, show it now instead
            // of blanking the panel for the length of a round-trip. The reload
            // is what makes it authoritative, and pendingResponseId outranks
            // this in resolveSelectedOpportunity if the two disagree.
            selectedOpportunity: ({ context, event }) =>
              context.opportunities.find((o) => o.responseId === event.responseId) ??
              context.selectedOpportunity,
            filter: config.defaultFilter,
          }),
          'setLoading',
          'clearError',
        ],
      },
      ON_SELECT: {
        target: '.$_LOAD_MESSAGES',
        guard: 'isValidSelection',
        actions: [
          assign({
            selectedOpportunity: ({ context, event }) =>
              context.opportunities.find((o) => o.responseId === event.responseId),
            messages: [] as TMessage[],
            pendingResponseId: undefined,
          }),
          'setMessagesLoading',
          'clearError',
        ],
      },
    },
    states: {
      $_VIEW: {
        entry: assign({ recoveryDeadlineAt: undefined }),
        on: {
          ON_ACKNOWLEDGE_UNCERTAINTY: {
            guard: ({ context }) =>
              context.uncertaintyReviewed &&
              context.uncertainAction?.responseId === context.selectedOpportunity?.responseId,
            actions: 'clearUncertainty',
          },
          ON_DISCARD_UNCERTAINTY: { actions: 'clearUncertainty' },
          ON_SEND_MESSAGE: {
            target: '$_SEND_MESSAGE',
            guard: 'canWrite',
            actions: 'captureAction',
          },
          ON_ACCEPT: {
            target: '$_UPDATE_STATUS',
            guard: 'canWrite',
            actions: 'captureAction',
          },
          ON_WITHDRAW: {
            target: '$_UPDATE_STATUS',
            guard: 'canWrite',
            actions: 'captureAction',
          },
        },
      },

      $_LOAD: {
        invoke: {
          src: 'loadOpportunities',
          input: ({ context }) => ({
            filter: context.filter,
            includeDeclined: context.includeDeclined,
            pendingResponseId: context.pendingResponseId,
            recoveryDeadlineAt: context.recoveryDeadlineAt,
          }),
          onDone: [
            {
              target: '$_LOAD_MESSAGES',
              guard: 'hasSelectableOpportunity',
              actions: [
                assign({
                  opportunities: ({ event }) => event.output.items,
                  totalCount: ({ event }) => event.output.totalCount,
                  includeDeclined: ({ event }) => event.output.includeDeclined,
                  selectedOpportunity: ({ context, event }) =>
                    resolveSelectedOpportunity(
                      context.selectedOpportunity,
                      event.output.items,
                      context.pendingResponseId
                    ),
                  messages: [] as TMessage[],
                  loading: false,
                  pendingResponseId: undefined,
                }),
                'setMessagesLoading',
              ],
            },
            {
              target: '$_VIEW',
              actions: assign({
                opportunities: ({ event }) => event.output.items,
                totalCount: ({ event }) => event.output.totalCount,
                includeDeclined: ({ event }) => event.output.includeDeclined,
                selectedOpportunity: ({ context, event }) =>
                  resolveSelectedOpportunity(
                    context.selectedOpportunity,
                    event.output.items,
                    context.pendingResponseId
                  ),
                messages: [] as TMessage[],
                loading: false,
                messagesLoading: false,
                pendingResponseId: undefined,
              }),
            },
          ],
          onError: {
            target: '$_VIEW',
            actions: assign({
              error: 'Failed to load opportunities. Please try again.',
              loading: false,
              messagesLoading: false,
            }),
          },
        },
      },

      $_LOAD_MESSAGES: {
        invoke: {
          src: 'loadMessages',
          input: ({ context }) => ({
            responseId: context.selectedOpportunity?.responseId,
            recoveryDeadlineAt: context.recoveryDeadlineAt,
          }),
          onDone: {
            target: '$_VIEW',
            actions: assign({
              messages: ({ event }) => event.output,
              messagesLoading: false,
              uncertaintyReviewed: ({ context }) =>
                context.uncertainAction !== null &&
                context.uncertainAction.responseId === context.selectedOpportunity?.responseId,
            }),
          },
          onError: {
            target: '$_VIEW',
            actions: assign({
              error: 'Failed to load messages. Please try again.',
              loading: false,
              messagesLoading: false,
            }),
          },
        },
      },

      $_SEND_MESSAGE: {
        on: {
          ON_LOAD: { actions: 'queueNavigation' },
          ON_FILTER: { actions: 'queueNavigation' },
          ON_INCLUDE_DECLINED: { actions: 'queueNavigation' },
          ON_SELECT: { guard: 'isValidSelection', actions: 'queueNavigation' },
          ON_DEEP_LINK: { actions: 'queueNavigation' },
        },
        invoke: {
          src: 'sendMessage',
          input: ({ context }) => ({
            responseId: context.pendingAction?.responseId,
            messageBody: context.pendingAction?.messageBody ?? '',
          }),
          onDone: {
            target: '$_AFTER_WRITE',
            actions: assign({
              retainedReplies: ({ context }) => {
                const retained = { ...context.retainedReplies };
                if (context.pendingAction) delete retained[context.pendingAction.responseId];
                return retained;
              },
              messages: ({ context, event }) => [...context.messages, event.output],
              error: null as string | null,
              sendReceipt: ({ context }) =>
                context.pendingAction
                  ? {
                      responseId: context.pendingAction.responseId,
                      sequence: (context.sendReceipt?.sequence ?? 0) + 1,
                    }
                  : context.sendReceipt,
              recoveryDeadlineAt: () => Date.now() + MARKET_READ_TIMEOUT_MS,
            }),
          },
          onError: {
            target: '$_AFTER_WRITE',
            actions: assign({
              retainedReplies: ({ context, event }) => {
                if (!context.pendingAction) return context.retainedReplies;
                const retained = { ...context.retainedReplies };
                if (isUncertainListingActionError(event.error)) {
                  // The uncertain attempt owns the body; a prior refusal no longer describes it.
                  delete retained[context.pendingAction.responseId];
                } else {
                  retained[context.pendingAction.responseId] = {
                    messageBody: context.pendingAction.messageBody ?? '',
                    error: replyRefusalFeedback(event.error, REPLY_REFUSED),
                  };
                }
                return retained;
              },
              uncertainAction: ({ context, event }) =>
                isUncertainListingActionError(event.error) ? context.pendingAction : null,
              // A refusal is kept with its response in retainedReplies and shown in
              // that response's composer (MCV-1702), so it never reaches the banner.
              error: ({ event }) =>
                isUncertainListingActionError(event.error)
                  ? 'We could not confirm whether your message was sent. Review the conversation before sending again.'
                  : null,
              recoveryDeadlineAt: ({ event }) =>
                isUncertainListingActionError(event.error)
                  ? Date.now() + MARKET_READ_TIMEOUT_MS
                  : undefined,
              loading: false,
              messagesLoading: false,
            }),
          },
        },
      },

      $_UPDATE_STATUS: {
        on: {
          ON_LOAD: { actions: 'queueNavigation' },
          ON_FILTER: { actions: 'queueNavigation' },
          ON_INCLUDE_DECLINED: { actions: 'queueNavigation' },
          ON_SELECT: { guard: 'isValidSelection', actions: 'queueNavigation' },
          ON_DEEP_LINK: { actions: 'queueNavigation' },
        },
        invoke: {
          src: 'updateStatus',
          input: ({ context }) => ({
            responseId: context.pendingAction?.responseId,
            expectedNegotiationId: context.pendingAction?.expectedNegotiationId,
            feeAuthorization: context.pendingAction?.feeAuthorization,
            action:
              context.pendingAction?.kind === 'accept'
                ? ('ACCEPT' as const)
                : ('WITHDRAW' as const),
          }),
          onDone: {
            target: '$_AFTER_WRITE',
            actions: assign({ recoveryDeadlineAt: () => Date.now() + MARKET_READ_TIMEOUT_MS }),
          },
          onError: {
            target: '$_AFTER_WRITE',
            actions: assign({
              uncertainAction: ({ context, event }) =>
                isUncertainListingActionError(event.error) ? context.pendingAction : null,
              // A fee refusal is not shown as an error: the view re-opens the fee dialog for it.
              feeRefusal: ({ event }) => feeRefusalCode(event.error),
              error: ({ event }) =>
                isUncertainListingActionError(event.error)
                  ? 'We could not confirm the status change. Review the conversation before acting again.'
                  : feeRefusalCode(event.error)
                    ? null
                    : (listingActionRefusalMessage(event.error) ??
                      'Failed to update status. Refresh before trying again.'),
              // A lost accept race is a definite refusal, but the row on screen is stale: reload it.
              recoveryDeadlineAt: ({ event }) =>
                isUncertainListingActionError(event.error) || acceptConflictCode(event.error)
                  ? Date.now() + MARKET_READ_TIMEOUT_MS
                  : undefined,
              loading: false,
              messagesLoading: false,
            }),
          },
        },
      },
      $_AFTER_WRITE: {
        entry: assign({ pendingAction: null }),
        always: [
          {
            guard: ({ context }) => context.queuedNavigation?.type === 'ON_SELECT',
            target: '$_LOAD',
            actions: [
              assign({
                pendingResponseId: ({ context }) =>
                  context.queuedNavigation?.type === 'ON_SELECT'
                    ? context.queuedNavigation.responseId
                    : undefined,
                recoveryDeadlineAt: ({ context }) =>
                  context.recoveryDeadlineAt ?? Date.now() + MARKET_READ_TIMEOUT_MS,
                queuedNavigation: null,
                error: null,
                uncertaintyReviewed: false,
              }),
              'setLoading',
            ],
          },
          {
            guard: ({ context }) => context.queuedNavigation !== null,
            target: '$_NAVIGATE',
            actions: raise(({ context }) => context.queuedNavigation ?? { type: 'ON_LOAD' }),
          },
          {
            guard: ({ context }) => context.recoveryDeadlineAt !== undefined,
            target: '$_LOAD',
            actions: 'setLoading',
          },
          { target: '$_VIEW' },
        ],
      },
      $_NAVIGATE: {},
    },
  });
}
